import type { HeadersFunction, LoaderFunction, MetaFunction } from '@remix-run/node';
import { json, redirect } from '@remix-run/node';
import { useOutletContext, useRouteLoaderData } from '@remix-run/react';

import { ComponentErrorBoundary, DefaultErrorBoundaryFallback } from '~/components/ComponentErrorBoundary';
import PostPaddingContainer from '~/components/Layout/PostPaddingContainer';
import BlueskyEmbed from '~/components/Scripts/BlueskyEmbed';
import StocktwitsEmbed from '~/components/Scripts/StocktwitsEmbed';
import TwitterEmbed from '~/components/Scripts/TwitterEmbed';
import useCreatePageView from '~/hooks/useLoaders/useCreatePageView';
import useRecommendedPosts from '~/hooks/useLoaders/useRecommendedPosts';
import usePreviewSimulation from '~/hooks/usePreviewSimulation';
import useSetLastViewedResource from '~/hooks/useSetLastViewedResource';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import type { OutletContext } from '~/interfaces/outlet_context';
import type { Paywall } from '~/interfaces/paywall';
import type { Post as IPost } from '~/interfaces/post';
import type { Publication } from '~/interfaces/publication';
import CommentsSection from '~/pages/post/CommentsSection';
import GatedContent from '~/pages/post/GatedContent';
import OnScrollActions from '~/pages/post/OnScrollActions';
import PostHTML from '~/pages/post/PostHTML';
import RecommendedPosts from '~/pages/post/RecommendedPosts';
import getPaywall from '~/services/api/paywalls/getPaywall.server';
import getDraftPost from '~/services/api/posts/getDraftPost.server';
import getPost from '~/services/api/posts/getPost.server';
import getPublication from '~/services/api/publication/getPublication.server';
import { loaderSession } from '~/session';
import getCanonicalUrl from '~/utils/getCanonicalUrl';
import getMergedMetaData from '~/utils/getMergedMetaData';
import getSameAsArray from '~/utils/getSameAsArray';
import setCacheHeaders from '~/utils/setCacheHeaders/index.server';
import { shouldRevalidateLoaderData } from '~/utils/shouldRevalidateLoaderData';

import useClickTracking from './_hooks/useClickTracking';
import { addTtsAudio } from './_utils/addTtsAudio';
import { getTruncatedHml } from './_utils/getTruncatedHtml';
import { processHtml } from './_utils/processHtml';
import { rewriteImages } from './_utils/rewriteImages';
import { stripHtml } from './_utils/stripHtml';
import { stripNoFollow } from './_utils/stripNoFollow';

export const meta: MetaFunction<typeof loader> = ({ matches, data }) => {
  const post = data?.post;
  const publication = data?.publication;
  const requestUrl = data?.requestUrl || '';
  const schemaUrl = getCanonicalUrl(requestUrl);

  if (!post || !publication) {
    return [
      { title: '404 - Not Found' },
      { description: 'The newsletter platform built for growth.' },
      { keywords: 'Newsletter, Email, Growth, Platform, SaaS, Creator' },
    ];
  }

  return getMergedMetaData({
    canonicalUrl: getCanonicalUrl(data?.requestUrl),
    matches,
    returnEarlyCheck: !post,
    overrides: {
      title: post?.meta_default_title || '',
      description: post?.meta_default_description || '',
      'og:url': `${publication?.url || ''}p/${post?.slug || ''}`,
      'og:title': post?.meta_og_title || '',
      'og:description': post?.meta_og_description || '',
      'og:site_name': publication?.name || '',
      'og:image': post?.image_url || publication?.thumbnail?.url || publication?.logo?.url || '',
      'og:image:alt': post?.web_subtitle || publication?.description || '',
      'twitter:url': `${publication?.url || ''}p/${post?.slug || ''}`,
      'twitter:title': post?.meta_twitter_title || '',
      'twitter:description': post?.meta_twitter_description || '',
      'twitter:image': post?.image_url || publication?.thumbnail?.url || publication?.logo?.url || '',
      'og:type': 'article',
      ...(post?.authors?.[0]?.name ? { author: post.authors[0].name, 'article:author': post.authors[0].name } : {}),
    },
    structuredData: [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        identifier: post.slug,
        url: schemaUrl,
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': schemaUrl,
        },
        headline: post.web_title,
        description: post.web_subtitle,
        datePublished: post.override_scheduled_at,
        dateModified: post.updated_at,
        isAccessibleForFree: post?.is_accessible_for_free ?? true,
        image: {
          '@type': 'ImageObject',
          url: post.image_url,
        },
        author: {
          '@type': 'Person',
          name: post.authors?.[0]?.name,
          image: {
            '@type': 'ImageObject',
            contentUrl: post.authors?.[0]?.profile_picture.url,
            thumbnailUrl: post.authors?.[0]?.profile_picture.thumb.url,
          },
        },
        publisher: {
          '@type': 'Organization',
          name: publication.name,
          url: publication.url,
          identifier: publication.id,
          sameAs: getSameAsArray(publication),
          logo: {
            '@type': 'ImageObject',
            url: publication.logo?.url,
            contentUrl: publication.logo?.url,
            thumbnailUrl: publication.logo?.thumb.url,
          },
          image: {
            '@type': 'ImageObject',
            url: publication.logo?.url,
            contentUrl: publication.logo?.url,
            thumbnailUrl: publication.logo?.thumb.url,
          },
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            item: {
              '@id': publication?.url,
              name: publication?.name,
            },
          },
          {
            '@type': 'ListItem',
            position: 2,
            item: {
              '@id': schemaUrl,
              name: post?.web_title,
            },
          },
        ],
      },
    ],
  });
};

// biome-ignore lint/correctness/noUnusedVariables: Keeping for backwards compatibility
export const headers: HeadersFunction = ({ actionHeaders, loaderHeaders, parentHeaders }) => {
  return {
    'cache-control': loaderHeaders.get('cache-control') || 'max-age=0', // no-cache
    'Cache-Tag': 'orchid-page',
  };
};

// biome-ignore lint/correctness/noUnusedVariables: Keeping for backwards compatibility
export const loader: LoaderFunction = async ({ request, params, context }: any) => {
  const { session } = await loaderSession(request);
  const url = new URL(request.url);
  const showComments = url.searchParams.get('comments') === 'true';
  const isDraft = url.searchParams.get('draft') === 'true';
  const giftContent = url.searchParams.get('gift_content');
  const currentSubscriberId = url.searchParams.get('current_subscriber_id');
  const tierIds = url.searchParams.getAll('tier_ids[]');
  const dynamicContentOverrides = url.searchParams.getAll('dynamic_content_overrides[]');
  // Swarm authorizes + sources the preview simulation from this token (minted by
  // Swarm for an authorized previewer); the raw tier/override params are ignored
  // server-side unless backed by it.
  const previewJwtToken = url.searchParams.get('preview_jwt_token');

  const promises = [getPublication(request, session)];

  if (isDraft) {
    promises.push(
      getDraftPost({
        request,
        session,
        slug: params.slug,
        currentSubscriberId,
        tierIds,
        dynamicContentOverrides,
        previewJwtToken,
      }),
    );
  } else {
    promises.push(getPost(request, session, params.slug, giftContent ? giftContent : undefined));
  }

  const [publication, post] = await Promise.all(promises);

  const isPremiumPost = post.audience === 'premium' || post.audience === 'both';
  let paywall = null;
  if (isPremiumPost && !post.force_premium) {
    paywall = await getPaywall(request, session, post.paywall_id);
  }

  if (post.redirect) {
    throw redirect(`/?${new URLSearchParams({ '404': url.pathname })}`); // Redirects to index feed when no post is found
  }

  // strip the <html><head><body> tags from the raw post html
  // replaces them with <div> tags
  let html = stripHtml(post.html);

  // post process the HTML by running it through a series of processor functions
  // 1. rewrite CDN image URLs to use the correct domain
  // 2. remove nofollow from links
  // 3. add text-to-speech audio to the post
  html = processHtml(html, [
    rewriteImages,
    publication.noindex_enabled === false ? stripNoFollow : null,
    post.text_to_speech_config?.audio_url && !post.hide_premium_post && !post.paywall_shown
      ? addTtsAudio(post.text_to_speech_config)
      : null,
  ]);

  return json(
    {
      post,
      publication,
      commentsEnabled: post.comments_enabled,
      recommendedReadingEnabled: publication.web_template.enable_recommended_reading,
      requestUrl: url.href,
      html,
      truncatedHtml: getTruncatedHml(html),
      onlyRenderComments: showComments,
      isDraft,
      paywall,
      currentSubscriberId,
      tierIds,
      dynamicContentOverrides,
    },
    {
      // Note: Keep MaxAge 0 to avoid caching in the browser, we only want to cache via the CDN with sMaxAge.
      headers: setCacheHeaders({
        maxAge: 0,
        sMaxAge: 10,
        staleWhileRevalidate: 10,
        publication,
      }),
    },
  );
};

interface PostLoaderData {
  post: IPost;
  publication: Publication;
  commentsEnabled: boolean;
  recommendedReadingEnabled: boolean;
  requestUrl: string;
  html: string;
  truncatedHtml: string;
  onlyRenderComments: boolean;
  isDraft: boolean;
  paywall: Paywall | null;
  currentSubscriberId: string;
  tierIds: string[];
  dynamicContentOverrides: string[];
}

export default function Post() {
  const {
    post,
    html,
    truncatedHtml,
    publication,
    recommendedReadingEnabled,
    commentsEnabled,
    onlyRenderComments,
    isDraft,
    paywall,
    tierIds,
    dynamicContentOverrides,
    currentSubscriberId,
  } = useTypedLoaderData<PostLoaderData>();
  const { subscription } = useRouteLoaderData<typeof loader>('root');
  const { cookies, setCookie, cookiesSet, isLoggedIn, admin } = useOutletContext<OutletContext>();

  const { isPopupDisabled, isGatedContentDisabled } = usePreviewSimulation({
    tierIds,
    currentSubscriberId,
    dynamicContentOverrides,
    cookies,
    isLoggedIn,
    isDraft,
  });

  // Create Page View
  const visitToken = cookies.visit_token ?? '';
  const pageViews = cookies.page_view;
  useCreatePageView({
    visitToken: visitToken,
    pageViews: pageViews || '',
    post,
    setCookie,
    cookiesSet,
  });

  useSetLastViewedResource({
    guid: `Post:${post.id}`,
    setCookie,
    cookiesSet,
  });

  // Misc. State checks
  const isPostPremium = post.audience === 'premium';
  const isAdmin = Boolean(admin);
  const isSubscriptionPremium = subscription?.tier === 'premium' && subscription?.status !== 'paused';
  const hidePremiumComments = isPostPremium && !isSubscriptionPremium && !isAdmin && !isDraft;
  const postPadding = publication.web_template.post_padding;
  const showTruncated = post.contains_subscriber_break && !isGatedContentDisabled;

  // Recommended Reading State
  const recommendedPosts = useRecommendedPosts(post.id, isDraft);
  const hasRecPosts = recommendedPosts?.length > 0;
  const hasRecommendedPosts = hasRecPosts && recommendedReadingEnabled;

  // setup click tracking for links
  useClickTracking({ post, visitToken });

  return (
    <div>
      {/** MISC */}
      {!onlyRenderComments && (
        <>
          <OnScrollActions
            post={post}
            publication={publication}
            isGatedContentDisabled={isGatedContentDisabled}
            isPopupDisabled={isPopupDisabled}
          />
          <TwitterEmbed updateOn={isPopupDisabled} />
          <BlueskyEmbed updateOn={isPopupDisabled} />
          <StocktwitsEmbed />
        </>
      )}
      {/** POST */}
      {!onlyRenderComments && (
        <>
          <PostHTML
            // show truncated html if subscriber gate is present and not logged in
            html={showTruncated ? truncatedHtml : html}
            isGatedContentDisabled={isGatedContentDisabled}
            subscription={subscription}
            admin={admin}
            post={post}
            publication={publication}
          />
          <div className="px-4">
            <GatedContent
              post={post}
              publication={publication}
              isGatedContentDisabled={isGatedContentDisabled}
              subscription={subscription}
              paywall={paywall}
            />
          </div>
        </>
      )}

      {/** COMMENTS + RECOMMENDED READING */}
      <div id="comments" className="px-4 space-y-8">
        {commentsEnabled && !hidePremiumComments && (
          <ComponentErrorBoundary
            fallbackRender={({ error }) => (
              <PostPaddingContainer classNames="mx-auto max-w-2xl" postPadding={postPadding}>
                <DefaultErrorBoundaryFallback error={error} />
              </PostPaddingContainer>
            )}
          >
            <CommentsSection post={post} subscription={subscription} publication={publication} />
          </ComponentErrorBoundary>
        )}

        {hasRecommendedPosts && !isDraft && (
          <RecommendedPosts publication={publication} recommendedPosts={recommendedPosts} />
        )}
      </div>
    </div>
  );
}

export const shouldRevalidate = shouldRevalidateLoaderData;
